import {
  createRootRoute,
  createRoute,
  createRouter,
  notFound,
  Outlet,
  redirect,
} from '@tanstack/react-router';

import { InvitePage } from '@/pages/InvitePage/InvitePage';
import { LoginPage } from '@/pages/LoginPage/LoginPage';
import { ConstantPage, NewConstantPage } from '@/pages/ConstantsPage/ConstantPage';
import { ConstantsPage } from '@/pages/ConstantsPage/ConstantsPage';
import { MediaPage } from '@/pages/MediaPage/MediaPage';
import { NotFoundPage } from '@/pages/NotFoundPage/NotFoundPage';
import { RecordPage } from '@/pages/RecordPage/RecordPage';
import { ComponentPage } from '@/pages/ComponentsPage/ComponentPage';
import { ComponentsPage } from '@/pages/ComponentsPage/ComponentsPage';
import { ModelPage, type ModelTab } from '@/pages/ModelsPage/ModelPage';
import { ModelsPage } from '@/pages/ModelsPage/ModelsPage';
import { NewTypePage } from '@/pages/ModelsPage/NewTypePage';
import { FormPage } from '@/pages/FormsPage/FormPage';
import { FormsPage } from '@/pages/FormsPage/FormsPage';
import { isFormTab, type FormTab } from '@/pages/FormsPage/tabs';
import { AutomationPage, NewAutomationPage } from '@/pages/AutomationsPage/AutomationPage';
import { AutomationsPage } from '@/pages/AutomationsPage/AutomationsPage';
import { UsersPage } from '@/pages/UsersPage/UsersPage';
import { isUsersTab, type UsersTab } from '@/pages/UsersPage/tabs';
import { ShopPage } from '@/pages/ShopPage/ShopPage';
import { isShopTab, type ShopTab } from '@/pages/ShopPage/tabs';
import { NavigationPage } from '@/pages/NavigationPage/NavigationPage';
import { PageFolderPage } from '@/pages/PagesPage/PageFolderPage';
import { PagesPage } from '@/pages/PagesPage/PagesPage';
import { TransferPage } from '@/pages/TransferPage/TransferPage';
import { PlatformActivityPage, SiteActivityPage } from '@/pages/ActivityPage/ActivityPage';
import { SettingsPage } from '@/pages/SettingsPage/SettingsPage';
import { isSettingsTab, type SettingsTab } from '@/pages/SettingsPage/tabs';
import { TeamPage } from '@/pages/TeamPage/TeamPage';
import { SiteHomePage } from '@/pages/SiteHomePage/SiteHomePage';
import { SitesPage } from '@/pages/SitesPage/SitesPage';
import { TenantsPage } from '@/pages/TenantsPage/TenantsPage';
import { getSession, refreshSession } from '@/shared/auth';

import { AppShell } from './layout/AppShell';
import { PublicLayout } from './layout/PublicLayout';
import { isEnvKey, type EnvKey } from './site/scope';

const rootRoute = createRootRoute({
  notFoundComponent: NotFoundPage,
});

/**
 * Открытая часть: вход и приём приглашения.
 *
 * Без оболочки админки — у человека ещё нет ни разделов, ни учётной записи,
 * и показывать ему боковое меню, по которому нельзя ходить, незачем.
 */
const publicRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public',
  component: PublicLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/login',
  component: LoginPage,
  // Куда вернуть после входа. Разбор, а не доверие: в адресной строке может
  // оказаться что угодно, включая ссылку на чужой сайт.
  //
  // Возвращается либо поле, либо пустой объект, а не поле со значением
  // undefined: иначе роутер считает параметр обязательным, и каждый переход
  // на вход пришлось бы сопровождать пустым search.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search['redirect'] === 'string' ? { redirect: search['redirect'] } : {},
});

const inviteRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/invite/$token',
  component: InvitePage,
});

/**
 * Защищённая часть.
 *
 * Проверка стоит на общем родителе, а не в каждом экране: экран, забывший
 * проверить вход, выглядит работающим ровно до первого запроса — и это
 * худший способ узнать об ошибке.
 */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  component: AppShell,
  beforeLoad: async ({ location }) => {
    // Токен доступа живёт в памяти вкладки и умирает вместе с ней, поэтому
    // при первой загрузке сеанс ещё неизвестен: его восстанавливают из
    // cookie. Ждём здесь, иначе перезагрузка страницы выкидывала бы на вход
    // человека, который из системы не выходил.
    if (getSession().status === 'unknown') {
      await refreshSession();
    }

    if (getSession().status !== 'authenticated') {
      // Именно throw: так устроен переход в роутере — он ловит это значение
      // и уводит на другой маршрут. Значение не ошибка, поэтому правило про
      // выброс исключений здесь неприменимо.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
});

// Корень уводит на сайты: отдельного обзора нет, а высаживать человека на
// пустую страницу ради того, чтобы он нажал ещё одну ссылку, незачем.
// Настоящий обзор появится, когда будет что показывать, — вместе с
// содержимым в фазе 1.
const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/sites' });
  },
});

const tenantsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/tenants',
  component: TenantsPage,
});

const sitesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sites',
  component: SitesPage,
});

/**
 * Сайт и окружение — в адресе: `/sites/<id>/prod/…`, `/sites/<id>/dev/…`.
 *
 * Всё, что внутри сайта, — дети этого маршрута: оболочка читает сайт и
 * окружение из адреса и по ним строит меню, крошки и полосу Development.
 * Неизвестное окружение в адресе — «не найдено», а не тихая подмена на
 * боевое: человек должен видеть, что ссылка неверна, а не править прод,
 * думая, что он на деве.
 */
const siteRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sites/$siteId/$env',
  component: Outlet,
  params: {
    parse: (raw: { siteId: string; env: string }): { siteId: string; env: EnvKey } => {
      if (!isEnvKey(raw.env)) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw notFound();
      }

      return { siteId: raw.siteId, env: raw.env };
    },
    stringify: (params: { siteId: string; env: EnvKey }) => params,
  },
});

const siteHomeRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/',
  component: SiteHomePage,
});

// Прежний адрес списка записей — теперь вкладка «Записи» деталки модели.
const collectionRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/c/$typeId',
  beforeLoad: ({ params }) => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/sites/$siteId/$env/models/$typeId', params });
  },
});

// Разделы-каталоги (решение Давида 20 октября 2026): плитки, правка — на
// деталке. Вкладка модели — в адресе.
const pagesRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/pages',
  component: PagesPage,
});

// Папка страниц: записи модели с адресом и блоками — статьи, кейсы, услуги.
const pageFolderRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/pages/$model',
  component: PageFolderPage,
});

const navigationRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/navigation',
  component: NavigationPage,
});

const modelsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/models',
  component: Outlet,
});

const modelsIndexRoute = createRoute({
  getParentRoute: () => modelsRoute,
  path: '/',
  component: ModelsPage,
});

const modelNewRoute = createRoute({
  getParentRoute: () => modelsRoute,
  path: '/new',
  component: () => <NewTypePage section="model" />,
});

const modelRoute = createRoute({
  getParentRoute: () => modelsRoute,
  path: '/$typeId',
  component: ModelPage,
  validateSearch: (search: Record<string, unknown>): { tab?: ModelTab } =>
    search['tab'] === 'fields' ||
    search['tab'] === 'stages' ||
    search['tab'] === 'rules' ||
    search['tab'] === 'settings'
      ? { tab: search['tab'] }
      : {},
});

// Формы (решение Давида 23 октября 2026): та же коллекция, что модель, но
// отдельным разделом — с заявками и подсказкой по API.
const formsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/forms',
  component: Outlet,
});

const formsIndexRoute = createRoute({
  getParentRoute: () => formsRoute,
  path: '/',
  component: FormsPage,
});

const formNewRoute = createRoute({
  getParentRoute: () => formsRoute,
  path: '/new',
  component: () => <NewTypePage section="form" />,
});

const formRoute = createRoute({
  getParentRoute: () => formsRoute,
  path: '/$typeId',
  component: FormPage,
  validateSearch: (search: Record<string, unknown>): { tab?: FormTab } =>
    isFormTab(search['tab']) ? { tab: search['tab'] } : {},
});

// Пользователи сайта (7.1): посетители, письма, настройки входа.
const usersRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/users',
  component: UsersPage,
  validateSearch: (search: Record<string, unknown>): { tab?: UsersTab } =>
    isUsersTab(search['tab']) ? { tab: search['tab'] } : {},
});

// Магазин (фаза 9): заказы и настройки оплаты.
const shopRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/shop',
  component: ShopPage,
  validateSearch: (search: Record<string, unknown>): { tab?: ShopTab } =>
    isShopTab(search['tab']) ? { tab: search['tab'] } : {},
});

// Автоматизации (7.4): событие → письмо, Telegram, вебхук.
const automationsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/automations',
  component: Outlet,
});

const automationsIndexRoute = createRoute({
  getParentRoute: () => automationsRoute,
  path: '/',
  component: AutomationsPage,
});

const automationNewRoute = createRoute({
  getParentRoute: () => automationsRoute,
  path: '/new',
  component: NewAutomationPage,
});

const automationRoute = createRoute({
  getParentRoute: () => automationsRoute,
  path: '/$automationId',
  component: AutomationPage,
});

const componentsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/components',
  component: Outlet,
});

const componentsIndexRoute = createRoute({
  getParentRoute: () => componentsRoute,
  path: '/',
  component: ComponentsPage,
});

const componentNewRoute = createRoute({
  getParentRoute: () => componentsRoute,
  path: '/new',
  component: () => <NewTypePage section="component" />,
});

const componentRoute = createRoute({
  getParentRoute: () => componentsRoute,
  path: '/$typeId',
  component: ComponentPage,
});

const recordRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/r/$recordId',
  component: RecordPage,
});

const mediaRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/media',
  component: MediaPage,
});

// Константы — телефон, почта, соцсети: значения, которые сайт берёт по ключу.
const constantsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/constants',
  component: Outlet,
});

const constantsIndexRoute = createRoute({
  getParentRoute: () => constantsRoute,
  path: '/',
  component: ConstantsPage,
});

const constantNewRoute = createRoute({
  getParentRoute: () => constantsRoute,
  path: '/new',
  component: NewConstantPage,
});

const constantRoute = createRoute({
  getParentRoute: () => constantsRoute,
  path: '/$constantId',
  component: ConstantPage,
});

// Вкладка настроек — в адресе: на «Подключение сайта» можно дать ссылку
// разработчику сайта.
const settingsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/settings',
  component: SettingsPage,
  validateSearch: (search: Record<string, unknown>): { tab?: SettingsTab } =>
    isSettingsTab(search['tab']) ? { tab: search['tab'] } : {},
});

const activityRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/activity',
  component: SiteActivityPage,
});

// Журнал всей платформы — вне сайта, только владельцу (проверяет сервер,
// пункт меню виден только ему).
const platformActivityRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/activity',
  component: PlatformActivityPage,
});

const teamRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/team',
  component: TeamPage,
});

// Прежний экран «Модели» со списком слева — теперь каталог.
const oldModelRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/model',
  validateSearch: (search: Record<string, unknown>): { type?: string } =>
    typeof search['type'] === 'string' ? { type: search['type'] } : {},
  beforeLoad: ({ params, search }) => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw search.type === undefined
      ? redirect({ to: '/sites/$siteId/$env/models', params })
      : redirect({
          to: '/sites/$siteId/$env/models/$typeId',
          params: { ...params, typeId: search.type },
          search: { tab: 'fields' },
        });
  },
});

// Перенос схемы — рядом с «Моделями»: схема прода меняется только им.
const transferRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/transfer',
  component: TransferPage,
});

// Дерево маршрутов вынесено отдельно от роутера: тесты собирают свой
// экземпляр с историей в памяти, а браузерный роутер один на приложение.
export const routeTree = rootRoute.addChildren([
  publicRoute.addChildren([loginRoute, inviteRoute]),
  authedRoute.addChildren([
    indexRoute,
    tenantsRoute,
    sitesRoute,
    platformActivityRoute,
    siteRoute.addChildren([
      siteHomeRoute,
      collectionRoute,
      recordRoute,
      mediaRoute,
      constantsRoute.addChildren([constantsIndexRoute, constantNewRoute, constantRoute]),
      pagesRoute,
      pageFolderRoute,
      navigationRoute,
      modelsRoute.addChildren([modelsIndexRoute, modelNewRoute, modelRoute]),
      formsRoute.addChildren([formsIndexRoute, formNewRoute, formRoute]),
      usersRoute,
      shopRoute,
      automationsRoute.addChildren([automationsIndexRoute, automationNewRoute, automationRoute]),
      componentsRoute.addChildren([componentsIndexRoute, componentNewRoute, componentRoute]),
      oldModelRoute,
      transferRoute,
      settingsRoute,
      teamRoute,
      activityRoute,
    ]),
  ]),
]);

export const router = createRouter({ routeTree });

// Регистрация типа роутера — именно она делает пути и параметры
// типизированными во всём приложении: опечатка в адресе перестаёт быть
// ошибкой рантайма и становится ошибкой сборки.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
